// components/climate/wildfire/WildfireSummary.tsx
import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { EmissionTier, MetricState, canonicalPeriodLabels, CanonicalPeriod } from '../../common'
import { usePropertyClimateData } from '../../../../hooks/climateData'
import { WildfireScoreBadge } from '../../common/fireRisk/utils'
import { useCurrentUser } from '../../../../hooks/users'
import ClimateMetricCard from '../../common/ClimateMetricCard'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'

const WF_SCORE_METRIC_KEY = 'wf:score'
const WF_AAL_METRIC_KEY = 'wf:damage_loss'
const WF_BI_DAYS_METRIC_KEY = 'wf:bi_days'

type PeriodCanonical = 'short' | 'mid' | 'long' | string

const PERIOD_ORDER: PeriodCanonical[] = ['short', 'mid', 'long']

function sortPeriods(periods: string[]): string[] {
  return [...periods].sort((a, b) => {
    const ia = PERIOD_ORDER.indexOf(a as PeriodCanonical)
    const ib = PERIOD_ORDER.indexOf(b as PeriodCanonical)
    if (ia === -1 && ib === -1) return a.localeCompare(b)
    if (ia === -1) return 1
    if (ib === -1) return -1
    return ia - ib
  })
}

function formatNumber(value: number | null | undefined, digits = 0) {
  if (value == null || Number.isNaN(value)) return '–'
  return value.toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  })
}

export default function WildfireSummary({
  propertyId,
  emissionTier,
}: {
  propertyId: number
  emissionTier: EmissionTier
}) {
  const {
    data: scoreData,
    isLoading: scoreLoading,
    error: scoreError,
  } = usePropertyClimateData(WF_SCORE_METRIC_KEY, { propertyId })

  const {
    data: aalData,
    isLoading: aalLoading,
    error: aalError,
  } = usePropertyClimateData(WF_AAL_METRIC_KEY, { propertyId })

  const {
    data: biData,
    isLoading: biLoading,
    error: biError,
  } = usePropertyClimateData(WF_BI_DAYS_METRIC_KEY, { propertyId })
  const { currencySymbol } = useCurrentUser()

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodCanonical>('mid')

  const loading = scoreLoading || aalLoading || biLoading
  const allErrored = scoreError && aalError && biError

  // Available periods from metrics that actually SUCCEEDED
  const availablePeriods: string[] = useMemo(() => {
    const periods: string[] = []

    if (scoreData && scoreData.state?.value === MetricState.SUCCEEDED) {
      periods.push(...(((scoreData.data?.keys?.period_canonical as string[] | undefined) || []) as string[]))
    }
    if (aalData && aalData.state?.value === MetricState.SUCCEEDED) {
      periods.push(...(((aalData.data?.keys?.period_canonical as string[] | undefined) || []) as string[]))
    }
    if (biData && biData.state?.value === MetricState.SUCCEEDED) {
      periods.push(...(((biData.data?.keys?.period_canonical as string[] | undefined) || []) as string[]))
    }

    let unique = Array.from(new Set(periods))
    if (!unique.length) {
      // Fallback: use canonical order, even if we might not have data
      unique = PERIOD_ORDER as string[]
    }
    return sortPeriods(unique)
  }, [scoreData, aalData, biData])

  const effectivePeriod: PeriodCanonical = useMemo(() => {
    if (availablePeriods.includes(selectedPeriod)) return selectedPeriod
    if (availablePeriods.includes('mid')) return 'mid'
    return availablePeriods[0]
  }, [availablePeriods, selectedPeriod])

  // ---- Score (simple single point per period/tier) ----
  let scoreValue: number | null = null
  const scoreSucceeded = scoreData?.state?.value === MetricState.SUCCEEDED

  if (scoreSucceeded) {
    const tierScoreData = scoreData?.data?.data?.[emissionTier] as
      | Record<string, { x: number; y: number } | { x: number; y: number }[]>
      | undefined

    if (tierScoreData) {
      const point = tierScoreData[effectivePeriod]
      if (Array.isArray(point)) {
        scoreValue = point[0]?.y ?? null
      } else if (point) {
        scoreValue = point.y
      }
    }
  }

  // ---- AAL (single y per period/tier) ----
  let aalValue: number | null = null
  const aalSucceeded = aalData?.state?.value === MetricState.SUCCEEDED

  if (aalSucceeded) {
    const tierAalData = aalData?.data?.data?.[emissionTier] as
      | Record<string, { x: number; y: number } | { x: number; y: number }[]>
      | undefined

    if (tierAalData) {
      const point = tierAalData[effectivePeriod]
      if (Array.isArray(point)) {
        aalValue = point[0]?.y ?? null
      } else if (point) {
        aalValue = point.y
      }
    }
  }

  // ---- BI days (single y per period/tier) ----
  let biValue: number | null = null
  const biSucceeded = biData?.state?.value === MetricState.SUCCEEDED

  if (biSucceeded) {
    const tierBiData = biData?.data?.data?.[emissionTier] as
      | Record<string, { x: number; y: number } | { x: number; y: number }[]>
      | undefined

    if (tierBiData) {
      const point = tierBiData[effectivePeriod]
      if (Array.isArray(point)) {
        biValue = point[0]?.y ?? null
      } else if (point) {
        biValue = point.y
      }
    }
  }

  if (loading && !scoreData && !aalData && !biData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={120}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (allErrored) {
    return <Alert severity="error">Could not load wildfire summary data.</Alert>
  }

  return (
    <Box>
      {/* Header + shared period control */}
      <Box
        mb={2}
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}>
        <ClimateSectionTitle title="Overview" subtitle="Summary of wildfire risk for the selected period" />

        {availablePeriods.length > 1 && (
          <ToggleButtonGroup
            size="small"
            value={effectivePeriod}
            exclusive
            onChange={(_, value) => {
              if (value) {
                setSelectedPeriod(value)
              }
            }}>
            {availablePeriods.map(p => (
              <ToggleButton key={p} value={p}>
                {canonicalPeriodLabels[p]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Cards row */}
      <Grid container spacing={2}>
        {/* Score */}
        <Grid item xs={12} md={4}>
          <ClimateMetricCard title="Risk Rating">
            {scoreSucceeded && scoreValue != null ? (
              <Stack spacing={0.5}>
                <WildfireScoreBadge score={scoreValue} size="compact" />
              </Stack>
            ) : !scoreSucceeded && scoreData ? (
              <Typography variant="body2" color="text.secondary">
                Wildfire risk rating not available for this period.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No wildfire risk rating data for this property.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>

        {/* AAL */}
        <Grid item xs={12} md={4}>
          <ClimateMetricCard title="AAL">
            {aalSucceeded && aalValue != null ? (
              <>
                <Typography variant="h4" component="div">
                  {`${currencySymbol}${formatNumber(aalValue, 2)}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Expected wildfire loss per year in this period.
                </Typography>
              </>
            ) : !aalSucceeded && aalData ? (
              <Typography variant="body2" color="text.secondary">
                AAL not available for this period.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No AAL data for this property.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>

        {/* BI days */}
        <Grid item xs={12} md={4}>
          <ClimateMetricCard title="Business Interruption">
            {biSucceeded && biValue != null ? (
              <>
                <Typography variant="h4" component="div">
                  {formatNumber(biValue, 1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Expected interruption days per year due to wildfire.
                </Typography>
              </>
            ) : !biSucceeded && biData ? (
              <Typography variant="body2" color="text.secondary">
                Business interruption data not available for this period.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No business interruption data for this property.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>
      </Grid>
    </Box>
  )
}
